/**
 * ModuleCustomFields — Generic custom fields renderer for any system module.
 *
 * Two usage modes:
 *
 * 1. Self-contained (recommended for new code):
 *    Internally loads field definitions and values via useCustomFields.
 *
 *    Detail (fully self-contained — no extra props needed):
 *      <ModuleCustomFields moduleKey="workers" recordId={id} mode="detail" />
 *
 *    Create (parent manages values for save-on-submit):
 *      <ModuleCustomFields moduleKey="workers" mode="create" values={v} onChange={setV} />
 *
 *    Edit (parent manages values; call saveValues via ref after save):
 *      const cfRef = useRef();
 *      <ModuleCustomFields ref={cfRef} moduleKey="workers" recordId={id} mode="edit" values={v} onChange={setV} />
 *      // in save handler:
 *      await cfRef.current?.saveValues(currentValues);
 *
 * 2. Prop-driven (backward compat — existing callers unchanged):
 *    Pass fields / sections / values / formType / loading as props.
 *    No internal hook is called; the parent manages all state.
 *      <ModuleCustomFields moduleKey="workers" fields={f} sections={s}
 *        values={v} onChange={setV} formType="edit" loading={l} />
 */
import React, { useEffect, useRef, useImperativeHandle } from 'react';
import { Box, Typography, Divider, LinearProgress } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DynamicFormRenderer from './DynamicFormRenderer';
import DynamicDetailRenderer from './DynamicDetailRenderer';
import useCustomFields from '../../hooks/useCustomFields';

const ModuleCustomFields = React.forwardRef(function ModuleCustomFields({
  moduleKey = '',
  recordId,           // needed for edit / detail mode
  mode,               // 'create' | 'edit' | 'detail'  (preferred)
  formType,           // backward-compat alias for mode
  // For create / edit: parent manages form state
  values: externalValues,
  onChange,
  // Prop-driven (backward compat) — providing these skips the internal hook
  fields: fieldsProp,
  sections: sectionsProp,
  loading: loadingProp,
}, ref) {
  const resolvedMode = mode || formType || 'create';

  // ── Internal hook ──────────────────────────────────────────────────────────
  // Only activated when field definitions are NOT provided as props.
  const useHookInternally = fieldsProp === undefined;
  const hookRecordId = useHookInternally && resolvedMode !== 'create' ? recordId : undefined;

  const {
    fields: hookFields,
    sections: hookSections,
    values: hookValues,
    loading: hookLoading,
    saveValues,
  } = useCustomFields(useHookInternally ? moduleKey : '', hookRecordId);

  // ── Seed parent state for edit mode ────────────────────────────────────────
  // When in edit mode using the internal hook, call onChange once with the
  // loaded record values so the parent's form state is pre-filled correctly.
  const seededRef = useRef(false);
  useEffect(() => { seededRef.current = false; }, [recordId]);
  useEffect(() => {
    if (!useHookInternally) return;
    if (resolvedMode === 'edit' && onChange && Object.keys(hookValues).length > 0 && !seededRef.current) {
      seededRef.current = true;
      onChange(hookValues);
    }
  }, [hookValues, resolvedMode, onChange, useHookInternally]);

  // ── Expose saveValues to parent via ref ────────────────────────────────────
  useImperativeHandle(ref, () => ({
    saveValues: (overrideValues) => saveValues(overrideValues),
  }), [saveValues]);

  // ── Resolve props vs hook values ───────────────────────────────────────────
  const fields   = fieldsProp   ?? hookFields;
  const sections = sectionsProp ?? hookSections;
  const loading  = loadingProp !== undefined ? loadingProp : hookLoading;

  // Detail mode uses values loaded from the hook; create/edit use parent-provided values.
  const displayValues = externalValues !== undefined ? externalValues : hookValues;

  // ── Visibility filtering ───────────────────────────────────────────────────
  const visibilityKey =
    resolvedMode === 'create' ? 'show_in_create'
    : resolvedMode === 'edit' ? 'show_in_edit'
    : 'show_in_detail';

  const customOnly    = fields.filter(f => f.is_custom);
  const visibleFields = customOnly.filter(f => f.is_active && f[visibilityKey]);

  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (visibleFields.length === 0) return null;

  const usedSectionIds   = new Set(customOnly.map(f => f.section_id).filter(Boolean));
  const relevantSections = sections.filter(s => usedSectionIds.has(s.id));

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: '0.9375rem',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          color: 'text.primary',
        }}
      >
        <SettingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        Fushat shtesë
      </Typography>

      {resolvedMode === 'detail' ? (
        <DynamicDetailRenderer
          fields={customOnly}
          sections={relevantSections}
          values={displayValues}
          moduleKey={moduleKey}
        />
      ) : (
        <DynamicFormRenderer
          fields={customOnly}
          sections={relevantSections}
          formType={resolvedMode}
          values={displayValues}
          onChange={onChange}
        />
      )}
    </Box>
  );
});

export default ModuleCustomFields;
